import React, { useCallback, useContext, useEffect, useMemo, useState  } from 'react';
import { Column }      from 'primereact/column';
import { DataTable }   from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText }   from 'primereact/inputtext';
import { Link } from 'react-router-dom';
import EventService    from '../../services/EventService';
import UserContext     from '../../contexts/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { normalizeLink } from '../../utils';

import './eventsReport.css';


export default function EventsReport() {
  const { currentUser } = useContext(UserContext);
  const [allEvents, setAllEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [registeredTrainings, setRegisteredTrainings] = useState([]);
  const [tab, setTab] = useState('all'); // 'all' or 'my'
  const [filterAll, setFilterAll] = useState('');
  const [categoryAll, setCategoryAll]= useState(null);

  const [filterTraining, setFilterTraining] = useState('');

  const [filterMyUpcoming, setFilterMyUpcoming]= useState('');
  const [categoryMyUpcoming, setCategoryMyUpcoming] = useState(null);

  const [filterMyPast, setFilterMyPast] = useState('');
  const [categoryMyPast, setCategoryMyPast] = useState(null);

  const [showLoginCredentialColumn, setShowLoginCredentialColumn] = useState(false);

  // PH-52 - phaus_member is the preferred check for the profile_type === Member
  const isPhausMember = useMemo(() => currentUser?.phaus_member, [currentUser?.phaus_member])

  const categories = [
    {label: 'All Categories', value: null},
    {label: 'Conference', value: 'Conference'},
    {label: 'Exam', value: 'Exam'},
    {label: 'Training', value: 'Training'},
    {label: 'Webinar', value: 'Webinar'},
  ]
  const normalizeAll = useCallback(e => ({
    id: e.id,
    name: e.campaign_name,
    category: e.category,
    start: e.start_date,
    end: e.end_date,
    ceus: e.ceus,
    format: e.event_mode,
    location: e.location,
    link: (isPhausMember && e.member_website_link) || e.website_link,
    membersOnly: e.members_only
  }),[isPhausMember]);
  
  const normalizeMy = useCallback(e => ({
    id: e.id,
    name: e.Campaign_Name,
    category: e.Category,
    start: e.Start_Date,
    end: e.End_Date,
    ceus: e.CEUs,
    format: e.Event_Mode,
    location: e.Location,
    link: (isPhausMember & e.Member_Website_Link) || e.Website_Link,
    membersOnly: e.Alliance_Member_Only
  }),[isPhausMember]);

  const normalizeTrainingRegistrations = useCallback(e => ({
    id: e?.id,
    phiusId: e?.Phius_ID,
    name: e?.Name,
    registrationDate: e?.Registration_Date,
    type: e?.Training_Type,
    sessionCode: e?.Session_Code,
    campaignName: e?.Campaign?.name || e?.Name,
    campaignId: e?.Campaign?.id || e?.id,
    start: e?.Start_Date,
    end: e?.End_Date,
    category: e?.Category,
    price: e?.Registration_Price,
    currency: e?.Currency,
    currencySymbol: e?.$currency_symbol,
  }),[]);

  const toDate = iso => new Date(`${iso}T00:00:00`);
  const now = new Date();
  const isUpcoming = e => {
    const endDate = toDate(e?.end);
    // add one full day (24 h) in milliseconds
    const endPlusOneDay = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
    return endPlusOneDay >= new Date();
  };
  const isPast = e => toDate(e?.end) <  now;

  const upcomingAll = allEvents.filter(isUpcoming).sort((a,b) => toDate(a.end) - toDate(b.end));
  const upcomingMy  = myEvents.filter(isUpcoming).sort((a,b) => toDate(a.end) - toDate(b.end));
  const pastMy      = myEvents.filter(isPast).sort((a,b) => toDate(b.end) - toDate(a.end));
  const upcomingTrainings = registeredTrainings.sort((a,b) => toDate(a.end) - toDate(b.end));

  const filteredAll = useMemo(() => {
    if (!categoryAll) return upcomingAll;
    return upcomingAll.filter(evt => evt.category === categoryAll);
  }, [upcomingAll, categoryAll]);

  const filteredMy = useMemo(() => {
    if (!categoryMyUpcoming) return upcomingMy;
    return upcomingMy.filter(evt => evt.category === categoryMyUpcoming);
  }, [upcomingMy, categoryMyUpcoming]);

  const filteredPast = useMemo(() => {
    if (!categoryMyPast) return pastMy;
    return pastMy.filter(evt => evt.category === categoryMyPast);
  }, [pastMy, categoryMyPast]);

  const dateBody = field => row => toDate(row[field]).toLocaleDateString();

  const registerBody = row => {
    if (row.membersOnly && tab === 'all' && !isPhausMember) {
      return <span>Members Only</span>;
    }
  
    const href = normalizeLink(row.link);
    return (
      <button
        className="button-cta-v2"
        type="button"
        onClick={() =>
          window.open(href, '_blank', 'noopener,noreferrer')
        }
      >
        Register
      </button>
    );
  };

  const myRegisterBody = row => {
    const href = normalizeLink(row.link);
    return (
      <button
        className="button-cta-v2"
        type="button"
        onClick={() =>
          window.open(href, '_blank', 'noopener,noreferrer')
        }
      >
        Register
      </button>
    );
  };

  const standardEventNameBody = row => (
    <span>
      {row?.name}
    </span>
  );
  const categoryBody = row => (
    <span className={`category-pill category-${row?.category?.toLowerCase()}`}>
      {row?.category}
    </span>
  );

  const priceBody = row => (
    <span className='price-cell'>{row?.price != null ? `${row?.currencySymbol}${Number(row?.price)?.toFixed(2)}` : ''}</span>
  );

  const nameLinkUpcomingEvents = (event) => {
    if (isPhausMember && event.membersOnly === true) {
      return (
        <Link
          to={`/details/event/${event.id}`}
          className='event-name-cell'
        >
          {event.name}
        </Link>
      );
    } else if (!isPhausMember && event.membersOnly === true) {
      return <p>{event.name}</p>;
    } else if (event.membersOnly === false) {
      return (
        <Link
          to={`/details/event/${event.id}`}
          className='event-name-cell'
        >
          {event.name}
        </Link>
      );
    }
  };

  useEffect(() => {
    let isCancelled = false;

    EventService.getAllEvents()
    .then(({ events }) => {
      if (!isCancelled) {
        setAllEvents(events.map(normalizeAll));
      }
    })
    .catch(err => {
      console.error('Failed to fetch all events:', err);
    });

  EventService.getMyEvents()
    .then(({ events = [] }) => {
      if (!isCancelled) {
        setMyEvents(events.map(normalizeMy));
      }
    })
    .catch(err => {
      console.error('Failed to fetch my events:', err);
    });

  EventService.getMyTrainings()
    .then(({ events = [] }) => {
      if (!isCancelled && events.length) {
        const regs = events.map(e => {
            const raw = e?.Login_Credentials;
            const credentials = raw === 'Refunded' || raw === 'Rescheduled' ? raw : null;
            return {...normalizeTrainingRegistrations(e), credentials};
        })
        setRegisteredTrainings(regs);

        const showColumn = regs?.some(reg => reg.credentials !== null);
        setShowLoginCredentialColumn(showColumn);
      }
    })
    .catch(err => {
      console.error('Failed to fetch trainings:', err);
    });

  return () => {
    isCancelled = true;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="events-report">
      <div className="tabs">
        <div
          className={`tab ${tab==='all'? 'active' : ''}`}
          onClick={() => setTab('all')}
        >
          All Events
        </div>
        <div
          className={`tab ${tab==='my'? 'active' : ''}`}
          onClick={() => setTab('my')}
        >
          My Events
        </div>
      </div>

      {tab === 'all' && (
        <>
          <div className="table-header">
            <h2>All Upcoming Events</h2>
            <div style={{ display: 'flex', flexDirection: 'column'}}>
            <label htmlFor="event-categories-all">Event Categories</label>
            <Dropdown
                id='event-categories-all'
                value={categoryAll}
                options={categories}
                onChange={e => setCategoryAll(e.value)}
                placeholder="All Categories"
                className="category-dropdown"
                showClear
            />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column'}}>
                <label htmlFor="search-all">Search</label>
                <InputText
                id="search-all"
                variant="filled"
                placeholder="Search…" 
                value={filterAll}
                onChange={e => setFilterAll(e.target.value)}
                className="search-input-field"
                />
            </div>
          </div>
          <DataTable
            value={filteredAll}
            paginator
            rows={5}
            globalFilter={filterAll}
            emptyMessage="No upcoming events"
            responsiveLayout="scroll"
            className="p-datatable-sm custom-datatable"
            tableStyle={{ backgroundColor: '#FEFEFE', color: '#2A2E33', fontSize: '14px', lineHeight: '20px'}}
          >
            <Column field="name"     header="Event Name" body={nameLinkUpcomingEvents} sortable/>
            <Column field="category" header="Category"   body={categoryBody} sortable/>
            <Column field="start"    header="Start Date" body={dateBody('start')} sortable/>
            <Column field="end"      header="End Date"   body={dateBody('end')} sortable/>
            <Column field="ceus"     header="CEUs"       sortable/>
            <Column field="format"   header="Format" sortable/>
            <Column field="location" header="Location" sortable/>
            <Column header="Register" body={registerBody} sortable/>
          </DataTable>
        </>
      )}

      {tab === 'my' && (
        <>
          <div className="table-header">
            <h2>Upcoming Events</h2>
            <div style={{ display: 'flex', flexDirection: 'column'}}>
            <label htmlFor="event-categories-my">Event Categories</label>
            <Dropdown
                id="event-categories-my"
                value={categoryMyUpcoming}
                options={categories}
                onChange={e => setCategoryMyUpcoming(e.value)}
                placeholder="All Categories"
                className="category-dropdown"
                showClear
            />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column'}}>
            <label htmlFor="search-my">Search</label>
            <InputText
              id='search-my'
              variant="filled"
              placeholder="Search…" 
              value={filterMyUpcoming}
              onChange={e => setFilterMyUpcoming(e.target.value)}
              className="search-input-field"
              showClear
            />
            </div>
          </div>
          <DataTable
            value={filteredMy}
            paginator
            rows={5}
            emptyMessage="No upcoming events"
            responsiveLayout="scroll"
            className="p-datatable-sm custom-datatable"
            tableStyle={{ backgroundColor: '#FEFEFE', color: '#2A2E33', fontSize: '14px', lineHeight: '20px'}}
          >
            <Column field="name"     header="Event Name" body={standardEventNameBody} sortable/>
            <Column field="category" header="Category"   body={categoryBody} sortable/>
            <Column field="start"    header="Start Date" body={dateBody('start')} sortable/>
            <Column field="end"      header="End Date"   body={dateBody('end')} sortable/>
            <Column field="ceus"     header="CEUs"       sortable/>
            <Column field="format"   header="Format"/>
            <Column field="location" header="Location"/>
            <Column header="My Registration" body={myRegisterBody}/>
          </DataTable>

          <div className="table-header" style={{ marginTop: '2rem' }}>
            <h2>Attended Events</h2>
            <div style={{ display: 'flex', flexDirection: 'column'}}>
            <label htmlFor="event-categories-past">Event Categories</label>
            <Dropdown
                id='event-categories-past'
                value={categoryMyPast}
                options={categories}
                onChange={e => setCategoryMyPast(e.value)}
                placeholder="All Categories"
                className="category-dropdown"
                showClear
            />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column'}}>
            <label htmlFor="search-past">Search</label>
            <InputText
                id="search-past"
                variant="filled"
              placeholder="Search…" 
              value={filterMyPast}
              onChange={e => setFilterMyPast(e.target.value)}
              className="search-input-field"
            />
            </div>
          </div>
          <DataTable
            value={filteredPast}
            paginator rows={5}
            emptyMessage="No past events"
            responsiveLayout="scroll"
            className="p-datatable-sm custom-datatable"
            tableStyle={{ backgroundColor: '#FEFEFE', color: '#2A2E33', fontSize: '14px', lineHeight: '20px'}}
          >
            <Column field="name"     header="Event Name" body={standardEventNameBody} sortable/>
            <Column field="category" header="Category"   body={categoryBody} sortable/>
            <Column field="start"    header="Start Date" body={dateBody('start')} sortable/>
            <Column field="end"      header="End Date"   body={dateBody('end')} sortable/>
            <Column field="ceus"     header="CEUs"       sortable/>
            <Column field="format"   header="Format"/>
            <Column field="location" header="Location"/>
          </DataTable>
          <button
              className={'external-link-button'}
              onClick={() => window.open(
                'https://forms.zohopublic.com/phius/form/SelfReportCEUs/formperma/vmEEjm8Jympa7RSb2OUOCYaN1fSYqFo4TBMTZKSbEr0',
                '_blank'
              )}
            >Report attendance to an event <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </button>
          <div className="table-header" style={{ marginTop: '2rem' }}>
            <h2>Registered Trainings</h2>
            <div style={{ display: 'flex', flexDirection: 'column'}}>
            <label htmlFor="search-trainings">Search</label>
            <InputText
                id="search-trainings"
                variant="filled"
                placeholder="Search…" 
                value={filterTraining}
                onChange={e => setFilterTraining(e.target.value)}
                className="search-input-field"
            />
            </div>
          </div>
          <DataTable
            value={upcomingTrainings}
            paginator
            rows={5}
            emptyMessage="No registered trainings"
            globalFilter={filterTraining}
            responsiveLayout="scroll"
            className="p-datatable-sm custom-datatable"
            tableStyle={{ backgroundColor: '#FEFEFE', color: '#2A2E33', fontSize: '14px', lineHeight: '20px'}}
          >
            <Column field="campaignName" header="Event Name" sortable/>
            <Column field="registrationDate" header="Registration Date" body={dateBody('registrationDate')} sortable/>
            <Column field="category" header="Category"   body={categoryBody} sortable/>
            <Column field="start" header="Start Date" body={dateBody('start')} sortable/>
            <Column field="end" header="End Date"   body={dateBody('end')} sortable/>
            <Column field="type" header="Registration Type" sortable/>
            <Column field="price" header="Registration Price" body={priceBody} sortable/>
            {showLoginCredentialColumn && <Column field="credentials" header="Status" sortable/>}
          </DataTable>
        </>
        
      )}
    </div>
  );
}
